import { isEmpty } from "lodash";
import React, { useState, useEffect } from "react";

import { Trans } from "react-i18next";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import ModelParametersAPI from "../../API/modelParameters.api";
import ModelsAPI from "../../API/models.api";
import HistogramChart from "../../components/Charts/HistogramChart";
import TitleWithDescription from "../../components/Headers/TitleWithDescription";
import ReferenceModal from "../../components/Modals/ReferenceModal";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import modelSchema from "../../schemas/model.schema";
import { SuccessAlert } from "../../utils/Alerts";
import { CreateHistogramPoints } from "../../utils/CreatePoints";
import { validate } from "../../utils/ValidationHelpers";
import ParametersForm from "../../components/Cards/ParametersForm";
import { MODEL_TYPE, UNITS } from "../../utils/constants";
import { getMappedParameterLocalizations } from "../../utils/generalMappers";

const ModelParameterForm = () => {
    //Form Data
    const [formData, setFormData] = useState([
        {
            displayName: "",
            name: "",
            localizations: { en: "" },
            parameterDetails: [
                {
                    unit: UNITS.percentage,
                    decimalPlaces: 2,
                },
            ],
        },
    ]);
    const [activeUnit, setActiveUnit] = useState([1]);

    //Reference Data
    const [data, setData] = useState({});
    const [existingDataReferences, setExistingDataReferences] = useState([]);
    const [dataReference, setDataReference] = useState({});
    const [dataReferenceDict, setDataReferenceDict] = useState({});
    const [modifiedDataReference, setModifiedDataReference] = useState({});
    const [showDataHistogram, setShowDataHistogram] = useState(false);
    const [dataHistogramPoints, setDataHistogramPoints] = useState([]);

    //Testing Reference Data
    const [testingData, setTestingData] = useState({});
    const [existingTestingDataReference, setExistingTestingDataReference] =
        useState([]);
    const [testingDataReference, setTestingDataReference] = useState({});
    const [testingDataReferenceDict, setTestingDataReferenceDict] = useState(
        {}
    );
    const [modifiedTestingDataReference, setModifiedTestingDataReference] =
        useState({
            headers: [],
            rows: [],
        });
    const [showTestingDataHistogram, setShowTestingDataHistogram] =
        useState(false);
    const [testingDataHistogramPoints, setTestingDataHistogramPoints] =
        useState([]);

    //Modals
    const [dataReferenceModal, setDataReferenceModal] = useState(false);
    const [testingDataReferenceModal, setTestingDataReferenceModal] =
        useState(false);

    //General
    const [isSubmitting, setIsSubmitting] = useState(false);

    //Validation
    const [error, setError] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const { t } = useTranslation();

    const location = useLocation();
    const history = useHistory();

    const modelId = location.state?.modelId || "";
    const modelType = location.state?.modelType || MODEL_TYPE.standard;
    const INDEX = 0;

    //TEMP COMMENT
    //CHANGE NAME TO DISPLAYNAME

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => <Trans i18nKey="addParameter">Add Parameter</Trans>,
        },
    ];

    useEffect(() => {
        if (!modelId) {
            history.push("/page-not-found");
        }
        getSampleNames();
    }, []);

    useEffect(() => {
        validate(
            formData,
            modelSchema.ParametersSettings,
            setIsFormValid,
            setError
        );
    }, [formData]);

    useEffect(() => {
        if (dataReference.rows) {
            getDataReferenceDict();
        }
    }, [dataReference]);

    useEffect(() => {
        if (testingDataReference.rows) {
            getTestingDataReferenceDict();
        }
    }, [testingDataReference]);

    useEffect(() => {
        setDataHistogramPoints(
            isEmpty(modifiedDataReference)
                ? []
                : CreateHistogramPoints(modifiedDataReference)
        );
    }, [modifiedDataReference]);

    useEffect(() => {
        setTestingDataHistogramPoints(
            isEmpty(modifiedTestingDataReference)
                ? []
                : CreateHistogramPoints(modifiedTestingDataReference)
        );
    }, [modifiedTestingDataReference]);

    const getDataReferenceDict = () => {
        const referenceDict = {};
        dataReference.rows.forEach((row) => {
            referenceDict[row.SampleName] = row[getFormDataValue("name")];
        });
        setDataReferenceDict(referenceDict);
    };

    const getTestingDataReferenceDict = () => {
        const referenceDict = {};
        testingDataReference.rows.forEach((row) => {
            referenceDict[row.SampleName] = getFormDataValue("name");
        });
        setTestingDataReferenceDict(referenceDict);
    };

    const getSampleNames = async () => {
        const result = await ModelsAPI.getSampleNames(modelId);
        if (isEmpty(result)) {
            history.push("/page-not-found");
        } else {
            setExistingDataReferences(result.samples);
            setData({
                headers: [],
                rows: result.samples.map((s) => [s.sampleName]),
            });
            setExistingTestingDataReference(result.testingSamples);
            setTestingData({
                headers: [],
                rows: result.testingSamples.map((s) => [s.sampleName]),
            });
        }
    };

    const submitParameter = async () => {
        setIsFormSubmitted(true);
        setIsSubmitting(true);

        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const result = await ModelParametersAPI.addModelParamter(
            formatPayload()
        );

        if (result?.inserted) {
            SuccessAlert(t("addParameterSuccesMessage"));
            history.goBack();
        }

        setIsSubmitting(false);
    };

    const mapReferences = (originalData, newDataDict) => {
        return originalData.map((row) => {
            const newReference = [
                ...row.reference,
                newDataDict[row.sampleName] || "--",
            ];

            let newRow = { ...row, reference: newReference };

            delete newRow.sampleName;

            return newRow;
        });
    };

    const getFormDataValue = (key, subKey) => {
        return (
            !isEmpty(formData) &&
            (subKey
                ? formData[INDEX]?.[key]?.[INDEX]?.[subKey]
                : formData[INDEX]?.[key])
        );
    };

    const getParameterDetails = () => {
        const parameter = [...formData[INDEX].parameterDetails];
        parameter.splice(0, 1);
        return {
            decimalPlaces:
                +getFormDataValue("parameterDetails", "decimalPlaces") ?? "",
            unitId: getFormDataValue("parameterDetails", "unit")?.value,
            units: parameter.map((unit, index) => ({
                unitConversionId: unit.unit.value,
                decimalPlaces: unit.decimalPlaces,
                displayUnit: index === activeUnit[0] - 1,
            })),
        };
    };

    const formatPayload = () => ({
        modelId,
        parameterName: getFormDataValue("name"),
        ...getParameterDetails(),
        calibrationReferences: mapReferences(
            existingDataReferences,
            dataReferenceDict
        ),
        calibrationReferencesEdited: !isEmpty(dataReference),
        testingReferences: mapReferences(
            existingTestingDataReference,
            testingDataReferenceDict
        ),
        testingReferencesEdited: !isEmpty(testingDataReference),
        modelParameterTranslations: getMappedParameterLocalizations(
            formData[INDEX]?.localizations
        ),
    });

    const showErrorMessage = (fieldName) => {
        return error[fieldName] && isFormSubmitted;
    };
    const fields = [
        {
            element: () => (
                <ParametersForm
                    parameters={formData}
                    setParameters={setFormData}
                    activeUnit={activeUnit}
                    setActiveUnit={setActiveUnit}
                    error={error}
                    isFormSubmitted={isFormSubmitted}
                    showClassificationOption={true}
                    hideParameterDeleteIcon={modelType === MODEL_TYPE.standard}
                />
            ),
        },
        {
            label: t("uploadDataReference"),
            element: () => (
                <>
                    <div className="action-header-wrapper flex items-center">
                        <button
                            disabled={!getFormDataValue("name")}
                            className="secondary-green-btn mr-2"
                            onClick={() =>
                                setDataReferenceModal((prev) => !prev)
                            }
                            // disabled={data == null}
                        >
                            <i className="fas fa-file-upload mr-2" />
                            <Trans i18nKey="uploadReferences">
                                Upload References
                            </Trans>
                        </button>
                        <button
                            onClick={() => setShowDataHistogram(true)}
                            className={`secondary-green-outline-btn ${
                                isEmpty(dataHistogramPoints) ? "disabled" : ""
                            }`}
                            disabled={isEmpty(dataHistogramPoints)}
                        >
                            <i className="fas fa-chart-bar mr-2" />
                            <Trans i18nKey="plotHistogram">
                                Plot Histogram
                            </Trans>
                        </button>
                    </div>
                </>
            ),
        },
        {
            label: t("uploadTestingDataReference"),
            element: () => (
                <>
                    <div className="action-header-wrapper flex items-center">
                        <button
                            disabled={
                                !getFormDataValue("name") ||
                                isEmpty(testingData.rows)
                            }
                            className="secondary-green-btn mr-2"
                            onClick={() =>
                                setTestingDataReferenceModal((prev) => !prev)
                            }
                        >
                            <i className="fas fa-file-upload mr-2" />
                            <Trans i18nKey="uploadTestingDataReference">
                                Upload Testing Data Reference
                            </Trans>
                        </button>
                        <button
                            onClick={() => setShowTestingDataHistogram(true)}
                            className={`secondary-green-outline-btn ${
                                isEmpty(testingDataHistogramPoints)
                                    ? "disabled"
                                    : ""
                            }`}
                            disabled={isEmpty(testingDataHistogramPoints)}
                        >
                            <i className="fas fa-chart-bar mr-2" />
                            <Trans i18nKey="plotHistogram">
                                Plot Histogram
                            </Trans>
                        </button>
                    </div>
                </>
            ),
        },
    ];

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div
                        className={`input-group ${
                            showErrorMessage(field.name)
                                ? "invalid-warning-input"
                                : ""
                        }`}
                        key={index}
                    >
                        <label
                            className={`uppercase ${
                                field.isRequired ? "required" : ""
                            }`}
                        >
                            {field.label}
                        </label>
                        {field.element()}
                    </div>
                );
            })
        );
    };
    return (
        <>
            <div
                className={`steps step1 ${
                    !showTestingDataHistogram && !showDataHistogram
                        ? "show"
                        : ""
                }`}
            >
                <BreadCrumb crumbs={breadCrumbs} />
                <TitleWithDescription
                    title={t("addParameter")}
                    description={t("addParameterDescription")}
                />
                <div className="card-table shadow flex flex-col w-full">
                    {renderFields()}
                </div>
                <div className="main-customer-form-bnt flex items-center justify-end mt-3">
                    <button
                        onClick={() => history.goBack()}
                        className="secondary-blue-btn mr-2"
                    >
                        <Trans i18nKey="cancel">Cancel</Trans>
                    </button>
                    <button
                        onClick={submitParameter}
                        className={`default-orange-outline-btn ${
                            isSubmitting || (isFormSubmitted && !isFormValid)
                                ? "disabled"
                                : ""
                        }`}
                    >
                        {t("add")}
                    </button>
                </div>
            </div>
            {/* Data upload reference modal */}
            <ReferenceModal
                isVisible={dataReferenceModal}
                setIsVisible={setDataReferenceModal}
                dataReference={dataReference}
                setReferenceData={setDataReference}
                data={data}
                setModifiedDataReference={setModifiedDataReference}
                entity="data"
                predictionHeader={getFormDataValue("name") || ""}
            />

            {/* Testing Data upload reference modal */}
            <ReferenceModal
                isVisible={testingDataReferenceModal}
                setIsVisible={setTestingDataReferenceModal}
                dataReference={testingDataReference}
                setReferenceData={setTestingDataReference}
                data={testingData}
                setModifiedDataReference={setModifiedTestingDataReference}
                entity="testingData"
                predictionHeader={getFormDataValue("name") || ""}
            />

            {/* Data histogram */}
            <HistogramChart
                points={dataHistogramPoints}
                visible={showDataHistogram}
                backCallback={() => {
                    setShowDataHistogram(false);
                }}
            />

            {/* Testing Data histogram */}
            <HistogramChart
                points={testingDataHistogramPoints}
                visible={showTestingDataHistogram}
                backCallback={() => {
                    setShowTestingDataHistogram(false);
                }}
            />
        </>
    );
};

export default ModelParameterForm;
